/**
 * The on-screen explanation for the pre-update backup.
 *
 * The backup downloads a JSON file with no warning, which from the employee's
 * side looks like the app randomly downloading something. Unexplained
 * downloads get dismissed, and on a phone they can get blocked outright — so
 * the one safety net that exists before new code touches the database is also
 * the thing most likely to be swiped away.
 *
 * Deliberately plain DOM, not React: this runs from src/index.tsx, outside the
 * component tree, and can fire at any moment including while React is
 * mid-render or has crashed. It has no dependencies on app state and cannot
 * be broken by one.
 */

const HOST_ID = 'ttl-update-notice';

/** Removes the banner if it is on screen. Safe to call when it isn't. */
export function hideUpdateNotice(): void {
  document.getElementById(HOST_ID)?.remove();
}

function render(html: string, background: string): void {
  // Server-side/test environments without a body: do nothing rather than throw
  // into the update path, which must never be blocked by its own notice.
  if (typeof document === 'undefined' || !document.body) return;

  hideUpdateNotice();
  const host = document.createElement('div');
  host.id = HOST_ID;
  host.setAttribute('role', 'status');
  host.setAttribute('aria-live', 'polite');
  // Inline styles on purpose: the stylesheet may not have loaded yet during an
  // update, and a notice nobody can read is worse than no notice.
  host.style.cssText = [
    'position:fixed',
    'left:0',
    'right:0',
    'bottom:0',
    'z-index:2147483647',
    `background:${background}`,
    'color:#ffffff',
    'padding:14px 16px',
    'font:15px/1.45 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif',
    'box-shadow:0 -2px 12px rgba(0,0,0,0.28)',
    'text-align:left',
  ].join(';');
  host.innerHTML = html;
  document.body.appendChild(host);
}

/** Shown while the backup file is being generated and downloaded. */
export function showBackupStarting(): void {
  render(
    '<strong>Updating Time&nbsp;Trakr&nbsp;Lite…</strong><br>' +
      'Saving a backup of your data first. A file will download — you can keep it ' +
      'or ignore it, it&rsquo;s just a safety copy.',
    '#1f4e79'
  );
}

/** Shown briefly after a successful backup, before the app reloads. */
export function showBackupFinished(): void {
  render('<strong>Backup saved.</strong> Updating now…', '#1f7a3f');
}

/**
 * Shown when the backup could not be written. The update still proceeds — the
 * failure is worth saying out loud, but it does not put existing data at risk,
 * and saying so prevents a scary-looking message from turning into a support
 * call.
 */
export function showBackupFailed(): void {
  render(
    '<strong>Couldn&rsquo;t save a backup this time.</strong><br>' +
      'Updating anyway — your saved time entries are not affected.',
    '#8a5a00'
  );
}
